/**
 * Composant d'affichage des annonces pour les espaces utilisateurs
 * Affiche les annonces actives avec possibilité de les fermer
 */
import React, { useState, useEffect } from 'react';
import { Badge } from './ui/badge';
import { X, Megaphone, ExternalLink, AlertTriangle, Info, Bell } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PRIORITY_STYLES = {
  low: {
    // Glassmorphism subtil — gris-bleu
    container: 'bg-gradient-to-br from-slate-50 via-white to-slate-50 border-slate-200',
    accent: 'from-slate-400 via-slate-500 to-slate-400',
    iconBg: 'bg-slate-100 border-slate-200',
    icon: 'text-slate-600',
    badge: 'bg-slate-700 text-white',
    label: 'INFO',
    eyebrow: 'text-slate-500',
  },
  normal: {
    // Bleu institutionnel
    container: 'bg-gradient-to-br from-blue-50/80 via-white to-sky-50/40 border-blue-200',
    accent: 'from-blue-400 via-sky-500 to-blue-400',
    iconBg: 'bg-gradient-to-br from-blue-500 to-sky-600 border-blue-300 shadow-md',
    icon: 'text-white',
    badge: 'bg-blue-700 text-white',
    label: 'NOUVEAUTE',
    eyebrow: 'text-blue-700',
  },
  high: {
    // Ambre — important
    container: 'bg-gradient-to-br from-amber-50 via-white to-yellow-50/40 border-amber-300',
    accent: 'from-amber-400 via-yellow-500 to-amber-400',
    iconBg: 'bg-gradient-to-br from-amber-500 to-orange-500 border-amber-300 shadow-md',
    icon: 'text-white',
    badge: 'bg-amber-600 text-white',
    label: 'IMPORTANT',
    eyebrow: 'text-amber-700',
  },
  urgent: {
    // Rouge — urgence
    container: 'bg-gradient-to-br from-red-50 via-white to-rose-50/40 border-red-300',
    accent: 'from-red-500 via-rose-500 to-red-500',
    iconBg: 'bg-gradient-to-br from-red-600 to-rose-700 border-red-300 shadow-md ring-2 ring-red-200',
    icon: 'text-white',
    badge: 'bg-red-600 text-white',
    label: 'URGENT',
    eyebrow: 'text-red-700',
  }
};

export default function AnnouncementsBanner({ space = 'public' }) {
  const { user: user, getAuthHeader, isAuthenticated } = useAuth();
  
  const [announcements, setAnnouncements] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnnouncements();
    
    // Charger les annonces déjà fermées du localStorage
    const savedDismissed = localStorage.getItem('dismissed_announcements');
    if (savedDismissed) {
      setDismissed(new Set(JSON.parse(savedDismissed)));
    }
  }, [space]);

  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/announcements/active/${space}`);
      setAnnouncements(res.data.announcements || []);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const dismissAnnouncement = async (id) => {
    // Ajouter à la liste des fermées localement
    const newDismissed = new Set([...dismissed, id]);
    setDismissed(newDismissed);
    localStorage.setItem('dismissed_announcements', JSON.stringify([...newDismissed]));
    
    // Si connecté, marquer côté serveur
    if (isAuthenticated) {
      try {
        await axios.post(
          `${BACKEND_URL}/api/admin/announcements/${id}/dismiss`,
          {},
          { headers: getAuthHeader() }
        );
      } catch {
        // Ignorer l'erreur, la fermeture locale suffit
      }
    }
  };

  // Filtrer les annonces déjà fermées
  const visibleAnnouncements = announcements.filter(a => !dismissed.has(a.id));

  if (loading || visibleAnnouncements.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3 mb-6" data-testid="announcements-banner">
      {visibleAnnouncements.map(announcement => {
        const styles = PRIORITY_STYLES[announcement.priority] || PRIORITY_STYLES.normal;
        const IconComponent = announcement.priority === 'urgent'
          ? AlertTriangle
          : announcement.priority === 'high'
            ? Bell
            : announcement.priority === 'low'
              ? Info
              : Megaphone;

        return (
          <div
            key={announcement.id}
            className={`relative overflow-hidden rounded-2xl border ${styles.container} shadow-sm hover:shadow-md transition-all duration-200`}
            data-testid={`announcement-${announcement.id}`}
          >
            {/* Bande superieure decorative */}
            <div className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${styles.accent}`} />
            {/* Halo decoratif */}
            <div className={`absolute -right-12 -top-12 w-40 h-40 rounded-full bg-gradient-to-br ${styles.accent} opacity-10 blur-3xl pointer-events-none`} />

            <div className="relative px-5 py-4">
              <div className="flex items-start gap-4">
                {/* Icon institutionnel */}
                <div className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center border ${styles.iconBg}`}>
                  <IconComponent className={`w-5 h-5 ${styles.icon}`} />
                </div>

                {/* Contenu */}
                <div className="flex-1 min-w-0">
                  {/* Eyebrow + Badge priorite */}
                  <div className="flex items-center gap-2 mb-1.5">
                    <span
                      className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${styles.eyebrow}`}
                      style={{ fontFamily: '"Outfit", system-ui, sans-serif' }}
                    >
                      {styles.label}
                    </span>
                    {(announcement.priority === 'urgent' || announcement.priority === 'high') && (
                      <Badge className={`${styles.badge} text-[9px] uppercase tracking-wider px-1.5 py-0 h-4`}>
                        {announcement.priority === 'urgent' ? 'A traiter' : 'A noter'}
                      </Badge>
                    )}
                  </div>

                  {/* Titre principal */}
                  <h3
                    className="text-base md:text-lg font-bold text-slate-900 leading-tight mb-1.5"
                    style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
                  >
                    {announcement.title}
                  </h3>

                  {/* Contenu */}
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {announcement.content}
                  </p>

                  {/* Lien CTA */}
                  {announcement.link_url && (
                    <a
                      href={announcement.link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-sm font-semibold text-slate-800 hover:border-slate-400 hover:bg-slate-50 transition-colors shadow-sm"
                    >
                      {announcement.link_text || 'En savoir plus'}
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>

                {/* Bouton fermer */}
                <button
                  className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-white/60 transition-all"
                  onClick={() => dismissAnnouncement(announcement.id)}
                  title="Fermer cette annonce"
                  data-testid={`dismiss-announcement-${announcement.id}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
